import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Clock, BarChart3, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import YocoPayment from '../components/YocoPayment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CourseDetail({ user }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  // Preload accessible exercise videos in the background
  useEffect(() => {
    if (exercises.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    exercises.forEach(ex => {
      if (!ex.access_required && ex.video_file) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = `${API}/videos/${ex.video_file}?token=${token}`;
        link.as = 'video';
        document.head.appendChild(link);
      }
    });

    return () => {
      document.querySelectorAll('link[rel="prefetch"][as="video"]').forEach(el => el.remove());
    };
  }, [exercises]);

  const fetchCourseData = async () => {
    try {
      const courseRes = await fetch(`${API}/courses/${courseId}`);
      if (courseRes.ok) {
        const courseData = await courseRes.json();
        setCourse(courseData);
        
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const exercisesRes = await fetch(`${API}/exercises`, { headers });
        if (exercisesRes.ok) {
          const allExercises = await exercisesRes.json();
          const courseExercises = allExercises.filter(ex => courseData.exercise_ids?.includes(ex.id));
          setExercises(courseExercises);
        }
      }
    } catch (error) {
      toast.error('Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    toast.success('Payment successful! Redirecting...');
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center">Loading...</div>;
  }

  if (!course) {
    return <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center">Course not found</div>;
  }

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="container mx-auto">
          <Button data-testid="back-to-courses-btn" onClick={() => navigate('/courses')} variant="ghost" className="rounded-full px-2 sm:px-4">
            <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Back to Courses</span><span className="sm:hidden">Back</span>
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12" data-testid="course-detail">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wide text-[#0B0B0B]/60 mb-2">{course.category}</div>
              <h1 className="text-2xl sm:text-4xl font-bold text-[#0B0B0B] mb-4" style={{ fontFamily: 'Clash Display' }}>{course.title}</h1>
              <div className="flex flex-wrap gap-2 sm:gap-4 items-center mb-4">
                <span className="bg-[#D9C7A3] text-[#0B0B0B] px-4 py-1 rounded-full text-sm font-bold">{course.difficulty}</span>
                <span className="flex items-center gap-1 text-gray-600">
                  <Clock className="w-4 h-4" /> {course.duration_weeks} weeks
                </span>
                <span className="flex items-center gap-1 text-gray-600">
                  <BarChart3 className="w-4 h-4" /> {exercises.length} exercises
                </span>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">{course.description}</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-[#0B0B0B] mb-6" style={{ fontFamily: 'Clash Display' }}>Exercises in This Program</h2>
              <div className="space-y-4">
                {exercises.map((exercise, index) => (
                  <motion.div
                    key={exercise.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => navigate(`/exercises/${exercise.id}`)}
                    className="cursor-pointer"
                    data-testid={`exercise-item-${index}`}
                  >
                    <Card className="p-6 bg-white rounded-2xl hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          exercise.access_required ? 'bg-gray-300' : 'bg-[#D9C7A3]'
                        }`}>
                          {exercise.access_required ? (
                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          ) : (
                            <Play className="w-6 h-6 text-[#0B0B0B]" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-[#0B0B0B] mb-1" style={{ fontFamily: 'Clash Display' }}>{exercise.title}</h3>
                            {exercise.access_required && (
                              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">Locked</span>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm mb-2">{exercise.description}</p>
                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>{exercise.duration_minutes} min</span>
                            <span className="capitalize">{exercise.difficulty}</span>
                            <span>{exercise.body_parts?.join(', ')}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Card className="p-6 bg-white rounded-2xl sticky top-6">
              <div className="text-4xl font-bold text-[#0B0B0B] mb-2">R{course.price.toFixed(2)}</div>
              <div className="text-gray-600 mb-6">{course.is_subscription ? 'per month' : 'one-time payment'}</div>
              
              {user ? (
                <YocoPayment
                  courseId={course.id}
                  courseName={course.title}
                  packageType="course"
                  onSuccess={handlePaymentSuccess}
                />
              ) : (
                <Button
                  onClick={() => {
                    navigate('/?login=true');
                  }}
                  className="w-full bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full py-6 text-lg font-bold"
                >
                  Login to Purchase
                </Button>
              )}

              {user && user.subscription_status !== 'active' && (
                <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                  <p className="text-gray-500 text-sm mb-3">or get access to everything</p>
                  <Button
                    data-testid="subscribe-alt-btn"
                    onClick={() => navigate('/subscription')}
                    variant="outline"
                    className="w-full rounded-full border-[#0B0B0B] text-[#0B0B0B] hover:bg-[#0B0B0B] hover:text-white"
                  >
                    <Crown className="w-4 h-4 mr-2" /> Subscribe for All Access
                  </Button>
                </div>
              )}

              <div className="border-t pt-6 mt-6 space-y-3">
                <h3 className="font-bold text-[#0B0B0B] mb-3">What's Included:</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>✓ {exercises.length} video demonstrations</div>
                  <div>✓ {course.duration_weeks}-week structured program</div>
                  <div>✓ Progress tracking tools</div>
                  <div>✓ Lifetime access to content</div>
                  <div>✓ Mobile & desktop compatible</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
